import { logger } from "@/common/logger";
import { ActivityAsideContent } from "@/components/ActivityAsideContent";
import {
  ActivityOverviewEmptySection,
  ActivityOverviewSection,
} from "@/components/ActivityOverviewSection";
import { AudienceGroups } from "@/components/AudienceGroups";
import EditMarkdownSection from "@/components/EditMarkdownSection";
import Markdown from "@/components/Markdown";
import { Rewards } from "@/components/Rewards";
import { Timeline } from "@/components/Timeline";
import { AsideRoot, AsideSection } from "@/kit/ui/aside";
import { BASE_TOC_TITLES } from "@/utils/activity";
import { extractMarkdownTitles, TOCItem } from "@/utils/generateToc";
import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Environment, graphql, useFragment, useMutation } from "react-relay";
import EventCompetitionsCarousel from "@/components/EventCompetitionsCarousel";
import { EventOverviewPageDescriptionsFragment$key } from "./__generated__/EventOverviewPageDescriptionsFragment.graphql";
import { EventOverviewPageLegacyDescriptionFragment$key } from "./__generated__/EventOverviewPageLegacyDescriptionFragment.graphql";
import { EventOverviewPageUpdateEventMutation as EventOverviewPageUpdateEventMutationType } from "./__generated__/EventOverviewPageUpdateEventMutation.graphql";
import { LoaderArgs } from "@/utils/route";
import { EventOverviewPageQuery as EventOverviewPageQueryType } from "./__generated__/EventOverviewPageQuery.graphql";
import { preload, usePreloaded } from "@/utils/relay";
import { ActivityNotFound } from "@/components/ActivityNotFound";
const [ABOUT, TIMELINE, PURPOSE, AUDIENCE, PRIZES, GETTING_STARTED] =
  BASE_TOC_TITLES;
const TRACKS = { depth: 0, text: "Tracks", id: "tracks" } as const;
const TITLES = Object.freeze([
  ABOUT,
  TIMELINE,
  PURPOSE,
  AUDIENCE,
  TRACKS,
  PRIZES,
  GETTING_STARTED,
] as const) satisfies ReadonlyArray<TOCItem>;
const EventOverviewPageUpdateEventMutation = graphql`
  mutation EventOverviewPageUpdateEventMutation(
    $id: ID!
    $input: UpdateEventInput!
  ) {
    updateEvent(id: $id, input: $input) {
      node {
        id
        description
      }
    }
  }
`;
const EventOverviewPageLegacyDescriptionFragment = graphql`
  fragment EventOverviewPageLegacyDescriptionFragment on Event {
    id
    description
    timeline {
      ...TimelineFragment
    }
    host {
      ...HostCardFragment
      events(first: 0) {
        totalCount
      }
    }
    ...TagListFragment
  }
`;
const EventOverviewPageDescriptionsFragment = graphql`
  fragment EventOverviewPageDescriptionsFragment on Event {
    about
    purpose
    gettingStarted
    timeline {
      ...TimelineFragment
    }
    audienceGroups {
      totalCount
      ...AudienceGroupsFragment
    }
    rewardCount: rewards {
      totalCount
    }
    host {
      ...HostCardFragment
      events(first: 0) {
        totalCount
      }
    }
    ...RewardsFragment
    ...TagListFragment
    ...EventCompetitionsCarouselFragment
  }
`;
export default function EventOverviewPage() {
  const { query } = useEventOverviewPageQuery();
  if (!query.eventBySlug) {
    return <ActivityNotFound kind="event" />;
  }
  const event = query.eventBySlug;
  return event.useLegacyDescription ? (
    <LegacyDescription event={event} viewerCanUpdate={event.viewerCanUpdate} />
  ) : (
    <DescriptionFields event={event} />
  );
}
interface LegacyDescriptionProps {
  event: EventOverviewPageLegacyDescriptionFragment$key;
  viewerCanUpdate: boolean;
}
function LegacyDescription(props: LegacyDescriptionProps) {
  const intl = useIntl();
  const [commitMutation, isMutationInFlight] =
    useMutation<EventOverviewPageUpdateEventMutationType>(
      EventOverviewPageUpdateEventMutation,
    );
  const { id, description, timeline, host, ...event } = useFragment(
    EventOverviewPageLegacyDescriptionFragment,
    props.event,
  );
  const titles = useMemo(
    () => extractMarkdownTitles(description ?? ""),
    [description],
  );
  return (
    <AsideRoot>
      <ActivityAsideContent
        titles={titles}
        host={host}
        taggable={event}
        count={host.events.totalCount}
        activity="EVENT"
      />
      <AsideSection>
        <div className="space-y-8">
          <ActivityOverviewSection
            title={<FormattedMessage defaultMessage="Timeline" />}
          >
            {timeline ? (
              <Timeline timeline={timeline} />
            ) : (
              <ActivityOverviewEmptySection field="timeline" />
            )}
          </ActivityOverviewSection>
          <ActivityOverviewSection
            title={<FormattedMessage defaultMessage="Description" />}
          >
            <EditMarkdownSection
              id={id}
              canEdit={props.viewerCanUpdate}
              disabled={isMutationInFlight}
              content={description ?? ""}
              fallback={<ActivityOverviewEmptySection field="description" />}
              onSubmit={({ content }) =>
                new Promise<void>((resolve, reject) =>
                  commitMutation({
                    variables: {
                      id,
                      input: { description: content || null },
                    },
                    onCompleted: () => resolve(),
                    onError: (error) => {
                      logger.error(error);
                      reject(
                        intl.formatMessage({
                          defaultMessage:
                            "An error occurred while saving the description. Please, try again later.",
                        }),
                      );
                    },
                  }),
                )
              }
              canUploadFiles
            />
          </ActivityOverviewSection>
        </div>
      </AsideSection>
    </AsideRoot>
  );
}
interface DescriptionFieldsProps {
  event: EventOverviewPageDescriptionsFragment$key;
}
function DescriptionFields(props: DescriptionFieldsProps) {
  const {
    about,
    purpose,
    gettingStarted,
    audienceGroups,
    timeline,
    rewardCount,
    host,
    ...event
  } = useFragment(EventOverviewPageDescriptionsFragment, props.event);
  return (
    <AsideRoot>
      <ActivityAsideContent
        titles={TITLES}
        host={host}
        taggable={event}
        count={host.events.totalCount}
        activity="EVENT"
      />
      <AsideSection>
        <div className="space-y-8">
          <ActivityOverviewSection id={ABOUT.id}>
            {about ? (
              <Markdown>{about}</Markdown>
            ) : (
              <ActivityOverviewEmptySection field="about" />
            )}
          </ActivityOverviewSection>

          <ActivityOverviewSection
            id={TIMELINE.id}
            title={<FormattedMessage defaultMessage="Timeline" />}
          >
            {timeline ? (
              <Timeline timeline={timeline} />
            ) : (
              <ActivityOverviewEmptySection field="timeline" />
            )}
          </ActivityOverviewSection>

          <ActivityOverviewSection
            id={PURPOSE.id}
            title={<FormattedMessage defaultMessage="Purpose" />}
          >
            {purpose ? (
              <Markdown>{purpose}</Markdown>
            ) : (
              <ActivityOverviewEmptySection field="purpose" />
            )}
          </ActivityOverviewSection>

          <ActivityOverviewSection
            id={AUDIENCE.id}
            title={<FormattedMessage defaultMessage="Participants" />}
          >
            {audienceGroups.totalCount === 0 ? (
              <ActivityOverviewEmptySection field="audience" />
            ) : (
              <AudienceGroups audience={audienceGroups} />
            )}
          </ActivityOverviewSection>

          <ActivityOverviewSection
            id={TRACKS.id}
            title={<FormattedMessage defaultMessage="Tracks" />}
          >
            <EventCompetitionsCarousel event={event} />
          </ActivityOverviewSection>

          <ActivityOverviewSection
            id={PRIZES.id}
            title={<FormattedMessage defaultMessage="Prizes" />}
          >
            {rewardCount.totalCount === 0 ? (
              <ActivityOverviewEmptySection field="prizes" />
            ) : (
              <Rewards activity={event} />
            )}
          </ActivityOverviewSection>

          <ActivityOverviewSection
            id={GETTING_STARTED.id}
            title={<FormattedMessage defaultMessage="Getting started" />}
          >
            {gettingStarted ? (
              <Markdown>{gettingStarted}</Markdown>
            ) : (
              <ActivityOverviewEmptySection field="getting started" />
            )}
          </ActivityOverviewSection>
        </div>
      </AsideSection>
    </AsideRoot>
  );
}
const EventOverviewPageQuery = graphql`
  query EventOverviewPageQuery($slug: String!) {
    eventBySlug(slug: $slug) {
      id
      useLegacyDescription
      competition {
        competition {
          slug
        }
      }
      viewerCanUpdate: viewerCan(action: UPDATE_EVENT)
      ...EventTeamsFragment
      ...EventOverviewPageLegacyDescriptionFragment
      ...EventOverviewPageDescriptionsFragment
    }
  }
`;
export const loader = (environment: Environment) => {
  return ({ params: { slug } }: LoaderArgs) => {
    if (!slug) {
      throw new Response("No slug", { status: 500 });
    }
    return preload<EventOverviewPageQueryType>(
      environment,
      EventOverviewPageQuery,
      { slug },
    );
  };
};
const useEventOverviewPageQuery = () =>
  usePreloaded<EventOverviewPageQueryType>();
